// src/components/subscription/MembershipPlans.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// import api from '../../utils/api';
import { useMembership } from '../../hooks/useMembership';

import { formatCurrency } from '../../utils/currency';
import axios from 'axios';
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 10000,
});

const MEMBERSHIP_PLANS = [
  {
    id: 'basic',
    name: 'Basic Plan',
    price: 2900, // $29.00
    credits: 500,
    features: ['500 credits per month', 'Basic analytics', 'Email support'],
  },
  {
    id: 'pro',
    name: 'Pro Plan',
    price: 7900, // $79.00
    credits: 2000,
    features: [
      '2000 credits per month',
      'Advanced analytics',
      'Priority support',
      'Custom branding',
    ],
  },
  {
    id: 'premium',
    name: 'Premium Plan',
    price: 14900, // $149.00
    credits: 5000,
    features: [
      '5000 credits per month',
      'API access',
      'Dedicated support',
      'Custom integrations',
    ],
  },
];

const MembershipPlans = () => {
  const navigate = useNavigate();
  // const { currentPlan, loading } = useMembership();
  const { currentPlan, loading, syncWithStripe } = useMembership();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [proratedAmount, setProratedAmount] = useState(null);

  useEffect(() => {
    // Sync with Stripe when component mounts
    const syncData = async () => {
      await syncWithStripe();
    };
    syncData();
  }, [syncWithStripe]);

  useEffect(() => {
    if (currentPlan) {
      const currentPlanDetails = MEMBERSHIP_PLANS.find(
        (p) => p.id === currentPlan.type
      );
      if (currentPlanDetails) {
        setSelectedPlan(currentPlanDetails);
      }
    }
  }, [currentPlan]);

  const calculateProration = async (newPlan) => {
    if (!currentPlan || currentPlan.type === 'free') return newPlan.price;

    try {
      const token = localStorage.getItem('ownerToken');

      const response = await api.post(
        '/subscriptions/calculate-proration',
        {
          currentPlanId: currentPlan.type,
          newPlanId: newPlan.id,
          remainingCredits: currentPlan.credits,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.proratedAmount;
    } catch (error) {
      console.error('Error calculating proration:', error);
      toast.error('Failed to calculate proration amount');
      return null;
    }
  };

  const handlePlanSelect = async (plan) => {
    if (currentPlan?.type === plan.id) {
      toast.info('You are already subscribed to this plan');
      return;
    }

    const currentPlanDetails = MEMBERSHIP_PLANS.find(
      (p) => p.id === currentPlan?.type
    );

    // Handle downgrade
    if (currentPlanDetails && currentPlanDetails.price > plan.price) {
      setSelectedPlan(plan);
      setShowDowngradeModal(true);
      return;
    }

    // Handle upgrade
    const proratedAmount = await calculateProration(plan);
    if (proratedAmount !== null) {
      setProratedAmount(proratedAmount);
      setSelectedPlan(plan);
      navigate('/owner/membership/checkout', {
        state: {
          plan,
          proratedAmount,
          isUpgrade: true,
        },
      });
    }
  };

  // const handleDowngradeConfirm = () => {
  //   navigate('/owner/membership/checkout', {
  //     state: {
  //       plan: selectedPlan,
  //       isDowngrade: true,
  //       effectiveDate: new Date(currentPlan.currentPeriodEnd),
  //     },
  //   });
  //   setShowDowngradeModal(false);
  // };

  const handleDowngradeConfirm = async () => {
    try {
      const token = localStorage.getItem('ownerToken');

      // Instead of going to checkout, directly schedule the downgrade
      const response = await api.post(
        '/subscriptions/create-payment-intent',
        {
          planId: selectedPlan.id,
          isDowngrade: true,
          effectiveDate: new Date(currentPlan.currentPeriodEnd),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        // Sync with Stripe to get updated membership details
        await syncWithStripe();

        // Show success message
        toast.success(
          `Your plan will be downgraded to ${selectedPlan.name} at the end of your current billing period.`
        );

        // Refresh membership data or redirect to membership page
        navigate('/owner/membership');
      }
    } catch (error) {
      console.error('Error scheduling downgrade:', error);
      toast.error('Failed to schedule downgrade. Please try again.');
    } finally {
      setShowDowngradeModal(false);
    }
  };

  // Modified downgrade modal JSX
  const DowngradeModal = () => (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white p-6 rounded-lg max-w-md'>
        <h3 className='text-lg font-bold mb-4'>Confirm Plan Change</h3>
        <p className='mb-4'>
          Your plan will be changed to {selectedPlan.name} at the end of your
          current billing period (
          {new Date(currentPlan.currentPeriodEnd).toLocaleDateString()}). You'll
          continue to have access to your current plan's features until then.
        </p>
        <p className='mb-4 text-sm text-gray-600'>
          Your new rate of {formatCurrency(selectedPlan.price / 100)}/month will
          start on {new Date(currentPlan.currentPeriodEnd).toLocaleDateString()}
          .
        </p>
        <div className='flex justify-end space-x-4'>
          <button
            onClick={() => setShowDowngradeModal(false)}
            className='px-4 py-2 text-gray-600 hover:text-gray-800'
          >
            Cancel
          </button>
          <button
            onClick={handleDowngradeConfirm}
            className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
          >
            Confirm Change
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className='flex justify-center items-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500' />
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8'>
      {/* Current Plan Banner */}
      {currentPlan && currentPlan.type !== 'free' && (
        <div className='bg-blue-50 border-l-4 border-blue-500 p-4 mb-8'>
          <p className='text-blue-700'>
            Current Plan:{' '}
            <span className='font-semibold capitalize'>{currentPlan.type}</span>
            {currentPlan.canceledAt && (
              <span className='ml-2'>
                (Cancels on{' '}
                {new Date(currentPlan.currentPeriodEnd).toLocaleDateString()})
              </span>
            )}
            <br />
            Remaining Credits: {currentPlan.credits}
          </p>
        </div>
      )}

      {/* Plan Cards */}
      <div className='grid md:grid-cols-3 gap-6'>
        {MEMBERSHIP_PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
              selectedPlan?.id === plan.id
                ? 'border-blue-500 shadow-lg'
                : 'border-gray-200'
            }`}
            onClick={() => handlePlanSelect(plan)}
          >
            <h3 className='text-xl font-bold mb-2'>{plan.name}</h3>
            <p className='text-3xl font-bold mb-4'>
              {formatCurrency(plan.price / 100)}
              <span className='text-sm font-normal'>/month</span>
            </p>
            <p className='text-lg mb-4'>{plan.credits} credits/month</p>

            <ul className='space-y-2'>
              {plan.features.map((feature, index) => (
                <li key={index} className='flex items-center'>
                  <svg
                    className='w-4 h-4 mr-2 text-green-500'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Downgrade Modal */}
      {/* {showDowngradeModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white p-6 rounded-lg max-w-md'>
            <h3 className='text-lg font-bold mb-4'>Confirm Downgrade</h3>
            <p className='mb-4'>
              Your plan will be downgraded to {selectedPlan.name} at the end of
              your current billing period. You'll continue to have access to
              your current plan's features until then.
            </p>
            <div className='flex justify-end space-x-4'>
              <button
                onClick={() => setShowDowngradeModal(false)}
                className='px-4 py-2 text-gray-600 hover:text-gray-800'
              >
                Cancel
              </button>
              <button
                onClick={handleDowngradeConfirm}
                className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
              >
                Confirm Downgrade
              </button>
            </div>
          </div>
        </div>
      )} */}
      {showDowngradeModal && <DowngradeModal />}
    </div>
  );
};

export default MembershipPlans;
